import { type MouseEvent } from "react";
import clsx from "clsx";
import { MainJournal } from "@/entities/journal";
import { useCoordinator } from "@/shared/lib/hooks";
import { useNavigate, routes } from "@/shared/lib/react-router";
import { TitleJournalStatusBadge } from "@/shared/ui";
import { formatDate, formatDateTime } from "@/shared/utils";
import "./journal-section-summary.scss";

interface SectionStats {
  total: number;
  new: number;
  rejected: number;
  signed: number;
  sent: number;
  awaiting: number;
}

interface SectionRowProps {
  label: string;
  stats: SectionStats;
}

interface StatusData {
  label: string;
  count: number;
}

const getSectionStats = (section: any): SectionStats => ({
  total:
    (section?.new ?? 0) +
    (section?.rejected ?? 0) +
    (section?.signed ?? 0) +
    (section?.sent ?? 0) +
    (section?.awaiting ?? 0),
  new: section?.new ?? 0,
  rejected: section?.rejected ?? 0,
  signed: section?.signed ?? 0,
  sent: section?.sent ?? 0,
  awaiting: section?.awaiting ?? 0,
});

const StatusCardRow = ({
  label,
  stats,
  onCardClick,
}: SectionRowProps & { onCardClick: (e: MouseEvent) => void }) => (
  <div
    className="journal-status-card"
    onClick={onCardClick}
  >
    <div className="journal-card-header">{label}</div>
    <div className="journal-card-content">
      <div className="journal-card-metrics">
        <div className="journal-metric-item">
          <span>Получено</span>
          <span className="journal-badge">{stats.new}</span>
        </div>
        <div className="journal-metric-item">
          <span>Обработано</span>
          <span className="journal-badge">{stats.rejected}</span>
        </div>
      </div>
    </div>
  </div>
);

const SectionCardRow = ({
  sectionNumber,
  statusList,
  onCardClick,
}: {
  sectionNumber: number;
  statusList: StatusData[];
  onCardClick: (sectionNumber: number, e: MouseEvent) => void;
}) => (
  <div
    className="journal-section-card"
    onClick={(e) => {
      e.stopPropagation();
      onCardClick(sectionNumber, e);
    }}
  >
    <div className="journal-section-card-header">Раздел №{sectionNumber}</div>
    <div className="journal-section-card-content">
      {statusList.map((status) => (
        <div
          key={`${sectionNumber}-${status.label}`}
          className="journal-status-row"
        >
          <span>{status.label}</span>
          <span className="journal-badge">{status.count}</span>
        </div>
      ))}
    </div>
  </div>
);

export const JournalSectionSummary = ({
  journalInfo,
  isClosing = false,
}: {
  journalInfo: MainJournal;
  isClosing?: boolean;
}) => {
  const navigate = useNavigate();
  const { projectId, objectId } = useCoordinator() as {
    projectId: string;
    objectId: string;
  };

  const handleSectionClick = (sectionNumber: number, e: MouseEvent) => {
    e.stopPropagation();
    const sectionRoute =
      `section${sectionNumber}` as keyof typeof routes.journals;

    navigate(
      routes.journals[sectionRoute](
        projectId,
        objectId,
        journalInfo.mainJournalId,
        journalInfo.titleJournal.titleId,
      ),
    );
  };

  const handleTitleClick = (e: MouseEvent) => {
    e.stopPropagation();
    navigate(
      routes.journals.title(
        projectId,
        objectId,
        journalInfo.mainJournalId,
        journalInfo.titleJournal.titleId,
      ),
    );
  };

  const handleNotesClick = (e: MouseEvent) => {
    e.stopPropagation();
    navigate(
      routes.journals.notes(
        projectId,
        objectId,
        journalInfo.mainJournalId,
        journalInfo.titleJournal.titleId,
      ),
    );
  };

  const handleDetailsClick = (e: MouseEvent) => {
    e.stopPropagation();
    navigate(
      routes.journals.details(
        projectId,
        objectId,
        journalInfo.mainJournalId,
        journalInfo.titleJournal.titleId,
      ),
    );
  };

  return (
    <div className={clsx("journal-summary-wrapper", isClosing && "closing")}>
      <h3 className="journal-summary-title">
        Сведения о регистрации титульного листа
      </h3>

      <div className="journal-status-cards-row">
        <div
          className="journal-status-card"
          onClick={handleTitleClick}
        >
          <div className="journal-card-header">Общие сведения</div>
          <div className="journal-card-content">
            <div className="journal-card-date">
              {formatDateTime(journalInfo.createDate)} МСК
            </div>
            <TitleJournalStatusBadge
              status={journalInfo.titleJournal.status}
            />
          </div>
        </div>

        <StatusCardRow
          label="Замечания"
          stats={{
            total: 0,
            new: journalInfo.commentsList?.received ?? 0,
            rejected: journalInfo.commentsList?.processed ?? 0,
            signed: 0,
            sent: 0,
            awaiting: 0,
          }}
          onCardClick={handleNotesClick}
        />

        <div
          className="journal-status-card"
          onClick={handleDetailsClick}
        >
          <div className="journal-card-header">Сведения о регистрации</div>
          <div className="journal-card-content">
            {journalInfo.registrationInfo?.buildingInfo ? (
              <div className="journal-card-registration">
                <div className="journal-card-date">
                  {formatDate(journalInfo.registrationInfo.buildingInfo.docDate)}
                </div>
                <div className="journal-card-number">
                  {journalInfo.registrationInfo.buildingInfo.docNumber}
                </div>
              </div>
            ) : (
              <div className="journal-card-no-data">
                Сведения не присланы
              </div>
            )}
          </div>
        </div>
      </div>

      <h3 className={clsx("journal-summary-title", "journal-summary-title--spaced")}>
        Сведения о разделах
      </h3>

      <div className="journal-section-cards-row">
        {[1, 2, 3, 4].map((sectionNumber) => {
          const stats = getSectionStats(
            journalInfo[`section${sectionNumber}` as keyof MainJournal],
          );

          return (
            <SectionCardRow
              key={sectionNumber}
              sectionNumber={sectionNumber}
              statusList={[
                { label: "Отклонено", count: stats.rejected },
                { label: "Ожидает", count: stats.awaiting },
                { label: "Подписано", count: stats.signed },
                { label: "Отправлено", count: stats.sent },
              ]}
              onCardClick={handleSectionClick}
            />
          );
        })}

        <SectionCardRow
          sectionNumber={5}
          statusList={[
            {
              label: "Подписано",
              count: journalInfo.section5?.signed ?? 0,
            },
            {
              label: "Отправлено",
              count: journalInfo.section5?.sent ?? 0,
            },
          ]}
          onCardClick={handleSectionClick}
        />

        <SectionCardRow
          sectionNumber={6}
          statusList={[
            {
              label: "Получено",
              count: journalInfo.section6?.received ?? 0,
            },
          ]}
          onCardClick={handleSectionClick}
        />
      </div>
    </div>
  );
};
